import {ExclamationCircleIcon, EyeIcon, EyeSlashIcon} from "@heroicons/react/20/solid";
import {forwardRef, InputHTMLAttributes, ReactNode, useId, useState} from "react";

/** The standard OTS text control surface, for inputs that are not built on this component. */
export const otsInputClassName = [
  // `!text-sm`: Mantine's reset otherwise sets inputs to 16px in the Mantine apps.
  "block min-h-10 w-full rounded-lg border bg-[var(--ots-field)] px-3 py-2 !text-sm text-[var(--ots-text)] shadow-xs transition-colors",
  "placeholder:text-[var(--ots-text-muted)]/70 focus:outline-none focus:ring-3",
  "read-only:bg-[var(--ots-background)] disabled:cursor-not-allowed disabled:bg-[var(--ots-background)] disabled:opacity-60"
].join(" ");

/** Border and focus ring for an OTS text control, as it looks normally and when flagged invalid. */
export const otsInputValidBorderClassName = "border-[var(--ots-field-outline)] hover:border-[var(--ots-outline)] focus:border-[var(--ots-primary)] focus:ring-[var(--ots-primary)]/15";
export const otsInputInvalidBorderClassName = "border-[var(--ots-error)] focus:border-[var(--ots-error)] focus:ring-[var(--ots-error)]/15";

export type OtsPasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  /** Rendered under the input and any error, e.g. a strength meter or requirements list. */
  footer?: ReactNode;
  containerClassName?: string;
};

/** A labelled password field with a show/hide toggle, so people can check what they typed. */
export const OtsPasswordInput = forwardRef<HTMLInputElement, OtsPasswordInputProps>(function OtsPasswordInput({
  label,
  description,
  error,
  footer,
  id,
  className,
  containerClassName,
  disabled,
  ...props
}, ref) {
  const generatedId = useId();
  const inputId = id ?? `ots-password-${generatedId}`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const [visible, setVisible] = useState(false);

  return (
    <div className={containerClassName}>
      <label htmlFor={inputId} className="block text-sm font-medium text-[var(--ots-text)]">
        {label}
      </label>
      {description && <p id={descriptionId} className="mt-1 text-xs text-[var(--ots-text-muted)]">{description}</p>}

      <div className="relative mt-1.5">
        <input
          ref={ref}
          id={inputId}
          type={visible ? "text" : "password"}
          disabled={disabled}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby={[descriptionId, errorId].filter(Boolean).join(" ") || undefined}
          className={[
            otsInputClassName,
            // Room for the toggle, and Edge's own reveal control hidden so there are not two.
            "pr-11 [&::-ms-reveal]:hidden",
            error ? otsInputInvalidBorderClassName : otsInputValidBorderClassName,
            className
          ].filter(Boolean).join(" ")}
          {...props}/>
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          disabled={disabled}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          aria-controls={inputId}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-[var(--ots-text-muted)] transition-colors hover:text-[var(--ots-primary)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--ots-primary)] disabled:pointer-events-none disabled:opacity-50">
          {visible
            ? <EyeSlashIcon aria-hidden="true" className="size-[18px]"/>
            : <EyeIcon aria-hidden="true" className="size-[18px]"/>}
        </button>
      </div>

      {error && (
        <p id={errorId} className="mt-1.5 flex items-start gap-1 text-xs font-medium text-[var(--ots-error)]">
          <ExclamationCircleIcon aria-hidden="true" className="mt-px size-3.5 shrink-0"/>
          <span>{error}</span>
        </p>
      )}
      {footer}
    </div>
  );
});
